import { PROVIDER_CONFIG } from "../../RGS/activeProviderConfig";
import { FEATURES_CONFIG } from "../../featuresConfig";
import { COIN_SYMBOL_IDS } from "../../SymbolData";

/**
 * scatterAnticipation — which reels anticipate the free spins trigger.
 *
 * Cleopatra's Fire (GDD §6.1, "Scatter Anticipation — Base Game and Free
 * Spins"): once one scatter short of the trigger has landed, every reel after
 * that one goes into anticipation, ONE BY ONE, as each reel's turn comes —
 * the Zeus tension principle (Vladimir, 2026-09-28: "when 2 land, all after
 * that should enter anticipation"). It runs at every speed, turbo included.
 *
 * The chain ends on the reel that brings the trigger scatter: from there the
 * trigger is certain and there is nothing left to wait for.
 *
 * This only answers the question about the BOARD. ReelControlContext plays
 * game_view's anticipation clips on each reel and holds that reel's stop.
 *
 * ── Read from the trigger contract, never re-stated ──
 *
 * `PROVIDER_CONFIG.FREE_SPINS_TRIGGER` says what a trigger is — which symbol,
 * on which reels, how many. Every number below comes from it.
 *
 * The board is known up front: the server sends the whole outcome before a
 * single reel stops.
 *
 * @param   {Object} round The adapted round, with `reels` as [reel][row].
 * @returns {{fromReel:number, reels:number[], hits:boolean}|null}
 *          null when there is nothing to anticipate. Otherwise:
 *          `fromReel`  the reel whose landing starts the chain — the one that
 *                      brings the scatter one short of the trigger.
 *          `reels`     the anticipation reels, in stop order.
 *          `hits`      true when the last of them brings the trigger scatter.
 */
export const scatterAnticipation = (round) => {
  if (!FEATURES_CONFIG.SCATTER_ANTICIPATION?.ENABLED) return null;

  const cfg = PROVIDER_CONFIG.FREE_SPINS_TRIGGER;
  const board = round?.reels;
  if (!cfg || !Array.isArray(board)) return null;

  const required = cfg.REQUIRED_SCATTERS;
  const expectAt = required - 1;
  if (!(expectAt >= 1)) return null;

  let count = 0;
  let fromReel = -1;
  let hitReel = -1;

  for (let reel = 0; reel < board.length; reel++) {
    const column = board[reel];
    if (!Array.isArray(column) || !cfg.SCATTER_REELS.includes(reel)) continue;
    for (let row = 0; row < column.length; row++) {
      const raw = column[row];
      const id = typeof raw === "string" ? parseInt(raw, 10) : raw;
      if (id !== cfg.SCATTER_ID) continue;
      count += 1;
      if (count >= expectAt && fromReel === -1) fromReel = reel;
      if (count >= required && hitReel === -1) hitReel = reel;
    }
  }

  if (fromReel === -1) return null;

  const lastReel = hitReel === -1 ? board.length - 1 : hitReel;
  const reels = [];
  for (let reel = fromReel + 1; reel <= lastReel; reel++) reels.push(reel);

  // The trigger completed on the very reel that brought the scatter before
  // it (two on one reel), or that reel is the last one: nothing to wait for.
  if (reels.length === 0) return null;

  return { fromReel, reels, hits: hitReel !== -1 };
};

/**
 * coinAnticipation — which reels anticipate the Hold & Win trigger.
 *
 * GDD §6.2: coins land anywhere, six or more trigger. The chain starts once
 * START_AT coins are down AND the reels still to stop can bring the rest
 * ("the remaining reels can complete a total of 6"), and ends on the reel that
 * brings the sixth — from there the trigger is certain. Same shape as
 * scatterAnticipation, so the same chain plays it.
 *
 * @param   {Object} round The adapted round, with `reels` as [reel][row].
 * @returns {{fromReel:number, reels:number[], hits:boolean}|null}
 */
export const coinAnticipation = (round) => {
  const feature = FEATURES_CONFIG.COIN_ANTICIPATION;
  if (!feature?.ENABLED) return null;

  const cfg = PROVIDER_CONFIG.HOLD_AND_WIN_TRIGGER;
  const board = round?.reels;
  if (!cfg || !Array.isArray(board)) return null;

  const required = cfg.REQUIRED_COINS;
  const startAt = feature.START_AT;

  let count = 0;
  let fromReel = -1;
  let hitReel = -1;

  for (let reel = 0; reel < board.length; reel++) {
    const column = board[reel];
    if (!Array.isArray(column)) continue;
    for (const raw of column) {
      const id = typeof raw === "string" ? parseInt(raw, 10) : raw;
      // if (cfg.COIN_IDS.includes(id)) count += 1;
      // The jackpot coin counts too — the adapter turns a trigger coin that
      // carries a jackpot into it (markJackpotCoins), and left out, six coins
      // read as five: the chain ran to the last reel on a trigger already
      // made (Vladimir, 2026-09-30).
      if (cfg.COIN_IDS.includes(id) || COIN_SYMBOL_IDS.includes(id)) count += 1;
    }

    if (hitReel === -1 && count >= required) hitReel = reel;

    if (fromReel === -1 && hitReel === -1 && count >= startAt) {
      // Cells left on the reels still to stop — can they bring the rest?
      const cellsLeft = board
        .slice(reel + 1)
        .reduce((sum, col) => sum + (Array.isArray(col) ? col.length : 0), 0);
      if (count + cellsLeft >= required) fromReel = reel;
    }
  }

  if (fromReel === -1) return null;

  const lastReel = hitReel === -1 ? board.length - 1 : hitReel;
  const reels = [];
  for (let reel = fromReel + 1; reel <= lastReel; reel++) reels.push(reel);
  if (reels.length === 0) return null;

  return { fromReel, reels, hits: hitReel !== -1 };
};

/**
 * anticipationChain — the ONE chain a spin plays, scatters and coins together.
 *
 * Both rules can fire on the same board; the anticipation still plays once.
 * The chain starts after the earlier of the two starting reels and runs to
 * the later of the two ending reels, with no gaps — every reel in between
 * takes its turn, so no reel is held without being given one.
 *
 * @param   {Object} round The adapted round.
 * @returns {{fromReel:number, reels:number[], hits:boolean}|null}
 */
export const anticipationChain = (round) => {
  // The Random Pot's presentation board is a reveal, not a chance: no
  // anticipation on it (RGS.randomPotPresentation).
  if (round?.randomPotPresentation) return null;
  // const chains = [scatterAnticipation(round), coinAnticipation(round)].filter(
  //   Boolean,
  // );
  // if (chains.length === 0) return null;
  // if (chains.length === 1) return chains[0];
  const scatterChain = scatterAnticipation(round);
  const coinChain = coinAnticipation(round);
  // Which feature the chain is waiting FOR — only those symbols glow during
  // it (Vladimir, 2026-09-30: "we let items glow only if there is a HOPE for
  // them to land"). 3 coins and 1 scatter: coins only; no free spins hope.
  const features = { scatters: !!scatterChain, coins: !!coinChain };
  const chains = [scatterChain, coinChain].filter(Boolean);
  if (chains.length === 0) return null;
  if (chains.length === 1) return { ...chains[0], features };

  const fromReel = Math.min(...chains.map((c) => c.fromReel));
  const lastReel = Math.max(...chains.map((c) => c.reels[c.reels.length - 1]));
  const reels = [];
  for (let reel = fromReel + 1; reel <= lastReel; reel++) reels.push(reel);
  return { fromReel, reels, hits: chains.some((c) => c.hits), features };
};

